import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useMeasureOptions } from '@/hooks/store/use-measure-options-store';
import type { MeasuredKeyRequirements } from '@/lib/piano/touch-design/measured-key.requirements';
export type MeasurePassId = 'down-up' | 'front-weight' | 'strike-weight';
export interface MeasurePass {
  /** The fields the pass asks for, in the order they are filled. */
  fields: (keyof MeasuredKeyRequirements)[];
  id: MeasurePassId;
  /** Shown before the label, as the mockups number the passes. */
  number: number;
}

/** Whether a key has every reading a pass asks for. */
export const isPassDone = (pass: MeasurePass, key: MeasuredKeyRequirements): boolean => pass.fields.every(field => key[field] !== null);

/**
 * How many keys of the keyboard each pass has done. The store may hold more
 * keys than the keyboard, from a larger one the profile had before.
 */
export const passProgress = (passes: readonly MeasurePass[], keys: readonly MeasuredKeyRequirements[], keyCount: number): Map<MeasurePassId, number> => new Map(passes.map(pass => [pass.id, keys.slice(0, keyCount).filter(key => isPassDone(pass, key)).length]));

/**
 * The three passes of a measuring session.
 *
 * A technician does not fill a key and move on; they go down the keyboard once
 * per quantity, gauge in hand. The table follows that: the pass in hand is at
 * full contrast, the rest recedes.
 *
 * Two of the quantities are optional, and a pass only claims one when its
 * option is on — otherwise its column would never leave the background, and
 * the pass would report no progress whatever the reader filled in.
 */
export const useMeasurePasses = () => {
  const $ = _c(8);
  const {
    useManualSWRMeasurements,
    useSupportSpringMeasurements
  } = useMeasureOptions();
  let t0;
  if ($[0] !== useSupportSpringMeasurements) {
    t0 = {
      fields: [...(useSupportSpringMeasurements ? ["downWeightWithSpringSupport"] as const : []), "downWeightWithoutSpringSupport", "upWeight"],
      id: "down-up",
      number: 1
    };
    $[0] = useSupportSpringMeasurements;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      fields: ["frontWeight"],
      id: "front-weight",
      number: 2
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== useManualSWRMeasurements) {
    t2 = {
      fields: ["strikeWeight", ...(useManualSWRMeasurements ? ["measuredStrikeWeightRatio"] as const : [])],
      id: "strike-weight",
      number: 3
    };
    $[3] = useManualSWRMeasurements;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t2) {
    t3 = [t0, t1, t2];
    $[5] = t0;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};